<template>
  <section class="submission-viewer">
    <header class="viewer-header">
      <div class="header-summary">
        <div>
          <span class="eyebrow">Litigation Submission</span>
          <h2>{{ submission.publicActionName }}</h2>
          <div class="header-meta">
            <span class="status-pill" :class="originalStatus">
              {{ statusLabel(originalStatus) }}
            </span>
            <span>{{ formattedDate(submission.completedAt) }}</span>
          </div>
        </div>

        <button
          type="button"
          class="back-button"
          :disabled="savingStatus || savingReview"
          @click="$emit('cancel')"
        >
          Back to Submissions
        </button>
      </div>

      <div class="header-actions">
        <button
          type="button"
          class="mark-reviewed-button"
          :disabled="loading || savingReview"
          @click="toggleReviewState"
        >
          {{ savingReview ? "Saving..." : needsReview ? "Mark Reviewed" : "Mark Needs Review" }}
        </button>

        <label class="status-field">
          <span>Submission Status</span>
          <select v-model="selectedStatus" :disabled="savingStatus">
            <option
              v-for="status in workflowStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
        </label>

        <button
          type="button"
          class="save-status-button"
          :disabled="savingStatus || !hasStatusChanged"
          @click="saveStatus"
        >
          {{ savingStatus ? "Saving..." : "Save Status" }}
        </button>
      </div>
    </header>

    <p v-if="statusMessage" class="status-message" :class="{ error: statusSaveFailed }">
      {{ statusMessage }}
    </p>

    <div v-if="loading" class="state-card">
      Loading submitted application...
    </div>

    <div v-else-if="loadError" class="state-card error">
      {{ loadError }}
    </div>

    <div v-else class="viewer-scroll">
      <section class="overview-grid">
        <article class="overview-card">
          <span>Client</span>
          <strong>{{ clientInfo?.name || "Unavailable" }}</strong>
          <small>{{ clientInfoError ? "Client information could not be loaded" : "App Client" }}</small>
        </article>

        <article class="overview-card">
          <span>Jurisdiction</span>
          <strong>{{ clientInfo?.jurisdiction || "Unavailable" }}</strong>
          <small>{{ clientInfoError ? "Client information could not be loaded" : "Current client record" }}</small>
        </article>

        <article class="overview-card">
          <span>Submitted</span>
          <strong>{{ relativeDate(submission.completedAt) }}</strong>
          <small>{{ formattedDate(submission.completedAt) }}</small>
        </article>

        <article class="overview-card">
          <span>Answers</span>
          <strong>{{ applicationSession.answers?.length ?? 0 }}</strong>
          <small>Structured application responses</small>
        </article>

        <article class="overview-card">
          <span>Agreement</span>
          <strong>{{ applicationSession.contractReviewedAt ? "Reviewed" : "Missing" }}</strong>
          <small>{{ agreementDetail }}</small>
        </article>

        <article class="overview-card">
          <span>Signature</span>
          <strong>{{ applicationSession.signatureCompletedAt ? "Signed" : "Missing" }}</strong>
          <small>{{ signatureDetail }}</small>
        </article>

        <article class="overview-card">
          <span>Client edited</span>
          <strong>{{ submissionActivity.editedOnDate ? "Updated" : "No updates" }}</strong>
          <small>{{ optionalDate(submissionActivity.editedOnDate) }}</small>
        </article>

        <article class="overview-card">
          <span>Last reviewed</span>
          <strong>{{ submissionActivity.lastReviewedByName || "Current review" }}</strong>
          <small>{{ optionalDate(submissionActivity.lastReviewedDate) }}</small>
        </article>
      </section>

      <section class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>Submitted Answers</h3>
            <p>Answers are displayed from the completed client application session.</p>
          </div>
          <span class="count-pill">{{ applicationSession.answers?.length ?? 0 }}</span>
        </div>

        <div v-if="answerGroups.length === 0" class="empty-state">
          No structured answers were found.
        </div>

        <div v-else class="answer-groups">
          <section v-for="group in answerGroups" :key="group.id" class="answer-group">
            <h4>{{ group.title }}</h4>

            <div class="answer-list">
              <article v-for="answer in group.answers" :key="answer.questionId" class="answer-card">
                <span class="question-number">{{ answer.categoryIndex + 1 }}</span>
                <div>
                  <p class="question-copy">{{ answer.questionSnapshot }}</p>
                  <p class="answer-copy">{{ answer.value }}</p>

                  <div
                    v-if="answerClarifications(answer).length"
                    class="answer-clarifications"
                  >
                    <article
                      v-for="clarification in answerClarifications(answer)"
                      :key="clarification.id"
                      class="answer-clarification"
                    >
                      <div class="clarification-heading">
                        <span>Client Clarification</span>
                        <time>{{ formattedDate(clarification.createdAt) }}</time>
                      </div>
                      <p>{{ clarification.text }}</p>
                    </article>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </div>
      </section>

      <section class="dashboard-card contract-card">
        <div class="section-heading">
          <div>
            <h3>Contract and Signature</h3>
            <p>Completion evidence saved with the Litigation application.</p>
          </div>
        </div>

        <div class="contract-grid">
          <div class="contract-detail">
            <span>Agreement completed</span>
            <strong>{{ optionalDate(applicationSession.agreementCompletedAt) }}</strong>
          </div>
          <div class="contract-detail">
            <span>Contract reviewed</span>
            <strong>{{ optionalDate(applicationSession.contractReviewedAt) }}</strong>
          </div>
          <div class="contract-detail">
            <span>Signature completed</span>
            <strong>{{ optionalDate(applicationSession.signatureCompletedAt) }}</strong>
          </div>
          <div class="contract-detail">
            <span>Application completed</span>
            <strong>{{ optionalDate(applicationSession.completedAt) }}</strong>
          </div>
        </div>

        <div v-if="applicationSession.signatureUrl" class="signature-preview">
          <span>Client Signature</span>
          <img :src="applicationSession.signatureUrl" alt="Client’s submitted signature" />
        </div>

        <div class="signed-contract-action">
          <button
            v-if="applicationSession.contractPdfStoragePath"
            type="button"
            class="view-contract-button"
            :disabled="openingContract"
            @click="viewSignedContract"
          >
            {{ openingContract ? "Opening Signed PDF..." : "View Signed Contract PDF" }}
          </button>

          <p v-else>
            A signed PDF was not archived for this application.
          </p>

          <small v-if="contractError" class="contract-error">
            {{ contractError }}
          </small>
        </div>
      </section>

      <section class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>Internal Notes</h3>
            <p>Visible only to administrative and legal review teams.</p>
          </div>
          <div class="section-heading-actions">
            <span class="count-pill">{{ notes.length }}</span>
            <button
              type="button"
              class="collapse-button"
              :aria-expanded="notesExpanded"
              @click="notesExpanded = !notesExpanded"
            >
              {{ notesExpanded ? "Hide" : "Show" }}
              <span :class="['collapse-chevron', { expanded: notesExpanded }]">⌄</span>
            </button>
          </div>
        </div>

        <div v-if="notesExpanded">
          <form class="entry-form" @submit.prevent="saveNote">
            <textarea
              v-model="noteText"
              rows="4"
              placeholder="Record review activity, follow-up needs, or other internal context..."
            />
            <div class="entry-footer">
              <span>This note will not be visible to the client.</span>
              <button type="submit" :disabled="savingNote || !noteText.trim()">
                {{ savingNote ? "Adding..." : "Add Internal Note" }}
              </button>
            </div>
          </form>

          <div v-if="notes.length" class="timeline-list">
            <article v-for="note in notes" :key="note.id" class="timeline-card">
              <div class="timeline-header">
                <span class="internal-pill">Internal Note</span>
                <span>{{ note.authorName || note.authorId || "Unknown reviewer" }}</span>
                <time>{{ formattedDate(note.createdAt) }}</time>
              </div>
              <p>{{ note.text }}</p>
            </article>
          </div>
          <div v-else class="empty-state contained">No internal notes have been added.</div>
        </div>
      </section>

      <section class="dashboard-card">
        <div class="section-heading">
          <div>
            <h3>Client Messages</h3>
            <p>Messages are delivered to the App Client and shown in their completed application.</p>
          </div>
          <div class="section-heading-actions">
            <span class="count-pill">{{ messages.length }}</span>
            <button
              type="button"
              class="collapse-button"
              :aria-expanded="messagesExpanded"
              @click="messagesExpanded = !messagesExpanded"
            >
              {{ messagesExpanded ? "Hide" : "Show" }}
              <span :class="['collapse-chevron', { expanded: messagesExpanded }]">⌄</span>
            </button>
          </div>
        </div>

        <div v-if="messagesExpanded">
          <form class="entry-form" @submit.prevent="saveMessage">
            <textarea
              v-model="messageText"
              rows="4"
              placeholder="Write a message to the App Client..."
            />
            <div class="entry-footer">
              <span>This message will be visible to the client.</span>
              <button type="submit" :disabled="savingMessage || !messageText.trim()">
                {{ savingMessage ? "Sending..." : "Send to Client" }}
              </button>
            </div>
          </form>

          <div v-if="messages.length" class="timeline-list">
            <article v-for="message in messages" :key="message.id" class="timeline-card">
              <div class="timeline-header">
                <span class="client-pill">Client Message</span>
                <span>{{ message.authorName || message.authorId || "Unknown reviewer" }}</span>
                <time>{{ formattedDate(message.createdAt) }}</time>
              </div>
              <p>{{ message.text }}</p>
            </article>
          </div>
          <div v-else class="empty-state contained">No messages have been sent to the client.</div>
        </div>
      </section>

      <p v-if="activityError" class="activity-error">{{ activityError }}</p>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, ref } from "vue";

import {
  litigationSubmissionWorkflowStatuses,
  litigationSubmissionWorkflowStatusLabel
} from "@/models/litigationSubmissions";

import {
  addLitigationSubmissionMessage,
  addLitigationSubmissionNote,
  fetchLitigationSubmissionMessages,
  fetchLitigationSubmissionNotes,
  fetchLitigationSubmissionClientInfo,
  fetchLitigationSubmissionSession,
  fetchSignedLitigationContractPDF,
  markLitigationSubmissionNeedsReview,
  markLitigationSubmissionReviewed,
  updateLitigationSubmissionStatus
} from "@/firebase/firestore";

export default {
  name: "SubmissionViewer",

  props: {
    submission: { type: Object, required: true },
    adminId: { type: String, default: "" },
    adminName: { type: String, default: "" }
  },

  emits: ["cancel", "reviewed", "status-saved"],

  setup(props, { emit }) {
    const applicationSession = ref({ answers: [] });
    const submissionActivity = ref(props.submission);
    const clientInfo = ref(null);
    const clientInfoError = ref(false);
    const notes = ref([]);
    const messages = ref([]);
    const loading = ref(true);
    const loadError = ref("");
    const activityError = ref("");
    const noteText = ref("");
    const messageText = ref("");
    const savingNote = ref(false);
    const savingMessage = ref(false);
    const originalStatus = ref(props.submission.status ?? "new");
    const selectedStatus = ref(originalStatus.value);
    const savingStatus = ref(false);
    const statusMessage = ref("");
    const statusSaveFailed = ref(false);
    const savingReview = ref(false);
    const openingContract = ref(false);
    const contractError = ref("");
    const notesExpanded = ref(true);
    const messagesExpanded = ref(true);

    const dateValue = value => {
      if (value?.toDate) { return value.toDate(); }
      if (value instanceof Date) { return value; }
      return value ? new Date(value) : null;
    };

    const formattedDate = value => {
      const date = dateValue(value);
      if (!date) { return "Not recorded"; }

      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
      }).format(date);
    };

    const optionalDate = value => value ? formattedDate(value) : "Not recorded";

    const relativeDate = value => {
      const date = dateValue(value);
      if (!date) { return "Unknown"; }
      const hours = Math.max(0, Math.floor((Date.now() - date.getTime()) / 3600000));
      if (hours < 1) { return "Less than an hour ago"; }
      if (hours < 24) { return `${hours} hour${hours === 1 ? "" : "s"} ago`; }
      const days = Math.floor(hours / 24);
      return `${days} day${days === 1 ? "" : "s"} ago`;
    };

    const workflowStatuses = litigationSubmissionWorkflowStatuses;
    const statusLabel = litigationSubmissionWorkflowStatusLabel;

    const hasStatusChanged = computed(() =>
      selectedStatus.value !== originalStatus.value
    );

    const needsReview = computed(() => {
      if (typeof submissionActivity.value.needsReview === "boolean") {
        return submissionActivity.value.needsReview;
      }

      const reviewedDate = dateValue(submissionActivity.value.lastReviewedDate);
      const editedDate = dateValue(submissionActivity.value.editedOnDate);

      if (!reviewedDate) { return true; }

      return editedDate ? editedDate > reviewedDate : false;
    });

    const agreementDetail = computed(() =>
      applicationSession.value.contractReviewedAt
        ? formattedDate(applicationSession.value.contractReviewedAt)
        : "No review timestamp"
    );

    const signatureDetail = computed(() =>
      applicationSession.value.signatureCompletedAt
        ? formattedDate(applicationSession.value.signatureCompletedAt)
        : "No signature timestamp"
    );

    const answerClarifications = answer => {
      if (!Array.isArray(answer?.clarifications)) { return []; }

      return answer.clarifications
        .filter(clarification => String(clarification?.text ?? "").trim())
        .slice()
        .sort((first, second) => {
          const firstDate = dateValue(first.createdAt)?.getTime() ?? 0;
          const secondDate = dateValue(second.createdAt)?.getTime() ?? 0;
          return firstDate - secondDate;
        });
    };

    const answerGroups = computed(() => {
      const definitions = [
        { id: "qualification", title: "Required Qualification", answers: [] },
        { id: "personalInjury", title: "Personal Injury", answers: [] },
        { id: "economicDamage", title: "Property or Economic Damage", answers: [] }
      ];

      (applicationSession.value.answers ?? []).forEach(answer => {
        const group = definitions.find(item => item.id === answer.category);
        if (group) { group.answers.push(answer); }
      });

      definitions.forEach(group => {
        group.answers.sort((first, second) => first.categoryIndex - second.categoryIndex);
      });

      return definitions.filter(group => group.answers.length);
    });

    const loadViewer = async () => {
      loading.value = true;
      loadError.value = "";
      clientInfoError.value = false;

      try {
        const clientInfoRequest = fetchLitigationSubmissionClientInfo(props.submission)
          .catch(error => {
            console.error("Unable to load Litigation submission client information:", error);
            clientInfoError.value = true;
            return null;
          });
        const [session, loadedNotes, loadedMessages, loadedClientInfo] = await Promise.all([
          fetchLitigationSubmissionSession(props.submission),
          fetchLitigationSubmissionNotes(props.submission),
          fetchLitigationSubmissionMessages(props.submission),
          clientInfoRequest
        ]);
        applicationSession.value = session;
        notes.value = loadedNotes;
        messages.value = loadedMessages;
        clientInfo.value = loadedClientInfo;
      } catch (error) {
        console.error("❌ Unable to load the submission viewer:", error);
        loadError.value = "Unable to load the completed Litigation application.";
      } finally {
        loading.value = false;
      }
    };

    const markReviewed = async () => {
      savingReview.value = true;
      activityError.value = "";

      try {
        const reviewedSubmission = await markLitigationSubmissionReviewed(
          submissionActivity.value,
          {
            adminId: props.adminId,
            adminName: props.adminName
          }
        );
        submissionActivity.value = reviewedSubmission;
        emit("reviewed", reviewedSubmission);
      } catch (error) {
        console.error("❌ Unable to mark the submission reviewed:", error);
        activityError.value = "Unable to mark this submission as reviewed.";
      } finally {
        savingReview.value = false;
      }
    };

    const markNeedsReview = async () => {
      savingReview.value = true;
      activityError.value = "";

      try {
        const updatedSubmission = await markLitigationSubmissionNeedsReview(
          submissionActivity.value,
          {
            adminId: props.adminId,
            adminName: props.adminName
          }
        );
        submissionActivity.value = updatedSubmission;
        emit("reviewed", updatedSubmission);
      } catch (error) {
        console.error("❌ Unable to mark the submission as needing review:", error);
        activityError.value = "Unable to mark this submission as needing review.";
      } finally {
        savingReview.value = false;
      }
    };

    const toggleReviewState = () =>
      needsReview.value ? markReviewed() : markNeedsReview();

    const saveNote = async () => {
      savingNote.value = true;
      activityError.value = "";
      try {
        const note = await addLitigationSubmissionNote(props.submission, {
          text: noteText.value,
          authorId: props.adminId,
          authorName: props.adminName
        });
        notes.value.unshift(note);
        noteText.value = "";
      } catch (error) {
        activityError.value = "Unable to save the internal note.";
      } finally {
        savingNote.value = false;
      }
    };

    const saveMessage = async () => {
      savingMessage.value = true;
      activityError.value = "";
      try {
        const message = await addLitigationSubmissionMessage(props.submission, {
          text: messageText.value,
          authorId: props.adminId,
          authorName: props.adminName
        });
        messages.value.unshift(message);
        messageText.value = "";
      } catch (error) {
        activityError.value = "Unable to send the message to the client.";
      } finally {
        savingMessage.value = false;
      }
    };

    const saveStatus = async () => {
      savingStatus.value = true;
      statusMessage.value = "";
      statusSaveFailed.value = false;

      try {
        const updatedSubmission = await updateLitigationSubmissionStatus(
          submissionActivity.value,
          selectedStatus.value,
          {
            adminId: props.adminId,
            adminName: props.adminName
          }
        );
        submissionActivity.value = updatedSubmission;
        originalStatus.value = updatedSubmission.status;
        statusMessage.value = `Status saved as ${statusLabel(updatedSubmission.status)}.`;
        emit("status-saved", updatedSubmission);
      } catch (error) {
        console.error("❌ Unable to update the submission status:", error);
        statusSaveFailed.value = true;
        statusMessage.value = "Unable to save the submission status.";
      } finally {
        savingStatus.value = false;
      }
    };

    const viewSignedContract = async () => {
      openingContract.value = true;
      contractError.value = "";
      const contractWindow = window.open("", "_blank");

      try {
        const signedContract = await fetchSignedLitigationContractPDF(
          props.submission
        );

        if (contractWindow) {
          contractWindow.opener = null;
          contractWindow.location.replace(signedContract.url);
        } else {
          window.open(signedContract.url, "_blank", "noopener,noreferrer");
        }

        window.setTimeout(
          () => URL.revokeObjectURL(signedContract.url),
          60000
        );
      } catch (error) {
        if (contractWindow) { contractWindow.close(); }
        console.error("❌ Unable to open the signed contract PDF:", error);

        const errorCode = String(error?.code ?? "");

        if (errorCode.includes("permission-denied") || errorCode.includes("unauthenticated")) {
          contractError.value = "Your administrator session is not authorized to open this PDF.";
        } else if (errorCode.includes("not-found")) {
          contractError.value = "The signed contract PDF could not be found.";
        } else if (errorCode.includes("data-loss")) {
          contractError.value = "The signed contract PDF did not pass its integrity check.";
        } else if (errorCode.includes("resource-exhausted")) {
          contractError.value = "The signed contract PDF is too large to open here.";
        } else {
          contractError.value = "Unable to open the signed contract PDF.";
        }
      } finally {
        openingContract.value = false;
      }
    };

    onMounted(loadViewer);

    return {
      applicationSession,
      submissionActivity,
      clientInfo,
      clientInfoError,
      notes,
      messages,
      loading,
      loadError,
      activityError,
      noteText,
      messageText,
      savingNote,
      savingMessage,
      originalStatus,
      selectedStatus,
      savingStatus,
      statusMessage,
      statusSaveFailed,
      savingReview,
      openingContract,
      contractError,
      notesExpanded,
      messagesExpanded,
      workflowStatuses,
      hasStatusChanged,
      needsReview,
      agreementDetail,
      signatureDetail,
      answerGroups,
      answerClarifications,
      formattedDate,
      optionalDate,
      relativeDate,
      statusLabel,
      toggleReviewState,
      saveStatus,
      viewSignedContract,
      saveNote,
      saveMessage
    };
  }
};
</script>

<style scoped>
.submission-viewer { height: 100%; min-height: 0; display: flex; flex-direction: column; }
.viewer-header { display: flex; flex-direction: column; gap: 20px; padding: 0 0 20px; border-bottom: 1px solid #e2e7e4; }
.header-summary { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
.eyebrow { color: #159c62; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.11em; text-transform: uppercase; }
.viewer-header h2 { margin: 5px 0 8px; color: #18231f; font-size: 1.55rem; }
.header-meta { display: flex; align-items: center; gap: 10px; color: #78827d; font-size: 0.85rem; }
.header-actions { display: flex; align-items: flex-end; justify-content: flex-end; gap: 9px; width: 100%; }
.status-field { display: flex; flex-direction: column; gap: 5px; color: #68736d; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.status-field select { min-width: 145px; padding: 10px 11px; border: 1px solid #d8dedb; border-radius: 9px; background: #fff; color: #29342f; font: inherit; text-transform: none; }
.back-button, .mark-reviewed-button, .save-status-button { min-height: 42px; padding: 11px 15px; border-radius: 9px; font: inherit; font-weight: 600; white-space: nowrap; cursor: pointer; }
.back-button { border: 1px solid #d8dedb; background: #fff; color: #29342f; }
.mark-reviewed-button { border: 1px solid #0b3652; background: #0b3652; color: #fff; }
.save-status-button { border: 1px solid #19aa6c; background: #19aa6c; color: #fff; }
.back-button:disabled, .mark-reviewed-button:disabled, .save-status-button:disabled { cursor: not-allowed; opacity: 0.5; }
.status-message { margin: 10px 0 0; color: #178554; font-size: 0.82rem; text-align: right; }
.status-message.error { color: #a33636; }
.viewer-scroll { min-height: 0; padding: 22px 4px 4px 0; overflow-y: auto; }
.overview-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 13px; }
.overview-card, .dashboard-card { border: 1px solid #dde2df; border-radius: 13px; background: #fff; }
.overview-card { display: flex; flex-direction: column; padding: 17px; }
.overview-card span, .contract-detail span { color: #7b8580; font-size: 0.73rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.overview-card strong { margin-top: 7px; color: #1d2924; font-size: 1rem; }
.overview-card small { margin-top: 5px; color: #818a86; }
.dashboard-card { margin-top: 18px; padding: 22px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.section-heading h3 { margin: 0; color: #1d2924; font-size: 1.08rem; }
.section-heading p { margin: 5px 0 0; color: #7b8580; font-size: 0.88rem; }
.section-heading-actions { display: flex; align-items: center; gap: 9px; }
.count-pill { min-width: 29px; padding: 6px 9px; border-radius: 999px; background: #edf4ef; color: #178554; font-size: 0.76rem; font-weight: 700; text-align: center; }
.collapse-button { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid #d8dedb; border-radius: 999px; background: #fff; color: #59635e; font: inherit; font-size: 0.76rem; font-weight: 700; cursor: pointer; }
.collapse-button:hover { background: #f7f9f8; }
.collapse-chevron { display: inline-block; font-size: 1rem; line-height: 0.8; transform: rotate(0deg); transition: transform 160ms ease; }
.collapse-chevron.expanded { transform: rotate(180deg); }
.answer-group { margin-top: 22px; }
.answer-group h4 { margin: 0 0 9px; color: #68736d; font-size: 0.76rem; letter-spacing: 0.08em; text-transform: uppercase; }
.answer-list { overflow: hidden; border: 1px solid #e4e8e6; border-radius: 10px; }
.answer-card { display: grid; grid-template-columns: 30px minmax(0, 1fr); gap: 12px; padding: 14px 16px; }
.answer-card + .answer-card { border-top: 1px solid #edf0ee; }
.question-number { display: flex; align-items: center; justify-content: center; width: 25px; height: 25px; border-radius: 50%; background: #edf4ef; color: #178554; font-size: 0.72rem; font-weight: 700; }
.question-copy { margin: 0; color: #65706a; font-size: 0.84rem; }
.answer-copy { margin: 6px 0 0; color: #1d2924; font-weight: 600; white-space: pre-wrap; }
.answer-clarifications { display: grid; gap: 8px; margin-top: 11px; }
.answer-clarification { padding: 10px 12px; border-left: 3px solid #f1b900; border-radius: 0 8px 8px 0; background: #fff9e7; }
.clarification-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; color: #8a6b0c; font-size: 0.7rem; }
.clarification-heading span { font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
.clarification-heading time { color: #847c65; }
.answer-clarification p { margin: 6px 0 0; color: #29342f; white-space: pre-wrap; }
.contract-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 18px; }
.contract-detail { display: flex; flex-direction: column; padding: 14px; border-radius: 9px; background: #f7f9f8; }
.contract-detail strong { margin-top: 6px; color: #29342f; font-size: 0.88rem; }
.signature-preview { margin-top: 17px; }
.signature-preview > span { display: block; margin-bottom: 8px; color: #68736d; font-size: 0.76rem; font-weight: 700; text-transform: uppercase; }
.signature-preview img { display: block; width: min(100%, 520px); height: 160px; object-fit: contain; border: 1px solid #e0e5e2; border-radius: 10px; background: #fafbfa; }
.signed-contract-action { display: flex; flex-direction: column; align-items: flex-start; gap: 7px; margin-top: 17px; }
.signed-contract-action p { margin: 0; color: #7b8580; font-size: 0.84rem; }
.signed-contract-action small { max-width: 100%; color: #7b8580; font-size: 0.7rem; overflow-wrap: anywhere; }
.view-contract-button { padding: 11px 15px; border: 1px solid #0b3049; border-radius: 9px; background: #0b3049; color: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.view-contract-button:disabled { cursor: not-allowed; opacity: 0.5; }
.signed-contract-action .contract-error { color: #a33636; }
.entry-form { margin-top: 18px; padding: 16px; border-radius: 10px; background: #f7f9f8; }
.entry-form textarea { width: 100%; box-sizing: border-box; padding: 12px; resize: vertical; border: 1px solid #d8dedb; border-radius: 8px; background: #fff; color: #1d2924; font: inherit; }
.entry-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 11px; color: #7b8580; font-size: 0.82rem; }
.entry-footer button { padding: 10px 14px; border: 0; border-radius: 8px; background: #19aa6c; color: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.entry-footer button:disabled { cursor: not-allowed; opacity: 0.5; }
.timeline-list { display: grid; gap: 10px; margin-top: 17px; }
.timeline-card { padding: 14px; border: 1px solid #e4e8e6; border-radius: 10px; }
.timeline-header { display: flex; align-items: center; gap: 8px; color: #7b8580; font-size: 0.75rem; }
.timeline-header time { margin-left: auto; }
.timeline-card p { margin: 10px 0 0; color: #29342f; white-space: pre-wrap; }
.internal-pill, .client-pill, .status-pill { padding: 5px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; }
.internal-pill { background: #eef0ef; color: #59635e; }
.client-pill, .status-pill { background: #eaf6ef; color: #178554; }
.status-pill.inReview { background: #fff2e7; color: #ad5514; }
.status-pill.needsInformation { background: #fff5d8; color: #8b6510; }
.status-pill.readyForReferral { background: #eaf1fb; color: #315d91; }
.status-pill.referralInProgress { background: #eee9fb; color: #65479b; }
.status-pill.placedWithPartner { background: #e6f6ef; color: #16794d; }
.status-pill.retainedInHouse { background: #e6f3f7; color: #22667a; }
.status-pill.notMovingForward, .status-pill.closed { background: #eef0ef; color: #66716b; }
.empty-state, .state-card { margin-top: 18px; padding: 22px; border: 1px solid #e0e5e2; border-radius: 10px; color: #78827d; text-align: center; }
.empty-state.contained { background: #fafbfa; }
.state-card.error, .activity-error { color: #a33636; }
.activity-error { margin: 14px 0 0; }
@media (max-width: 1050px) { .overview-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 900px) { .header-summary, .header-actions { align-items: stretch; flex-direction: column; } }
@media (max-width: 700px) { .entry-footer { align-items: stretch; flex-direction: column; } .overview-grid, .contract-grid { grid-template-columns: 1fr; } }
</style>
